import DeviceInfo from 'react-native-device-info';
// Variant identity (names, package/bundle ids, allowed modes, default mode) lives
// in a dependency-free manifest so the Expo build config (app.config.js) can read
// the SAME table from Node. Keep all variant data edits in that manifest.
import {APP_VARIANTS, MINI_APPS, MODULE_HOME_ROUTES, resolveMiniAppIdFromEnv} from './appVariantManifest';
// Build-time switchboard: a release can drop a whole operational mode (see
// config/featureFlags.js) on top of what the variant manifest bundles.
import {isFeatureEnabled} from './featureFlags';
// Phones open restaurant mode on the POS Hub; tablets / Classic keep the quick receipt screen.
import {getPosEntryRoute} from '../helpers/posEntryRoute';

export {APP_VARIANTS, MINI_APPS, MODULE_HOME_ROUTES};

const PACKAGE_TO_VARIANT = Object.values(APP_VARIANTS).reduce((items, variant) => {
    items[variant.packageId] = variant.id;
    // A variant may ship a DIFFERENT iOS bundle id (`iosPackageId`); index it too,
    // otherwise DeviceInfo.getBundleId() on that build resolves to no variant and
    // the app silently boots as `global` with no error.
    if (variant.iosPackageId) {
        items[variant.iosPackageId] = variant.id;
    }
    (variant.legacyPackageIds || []).forEach((legacyId) => {
        items[legacyId] = variant.id;
    });
    return items;
}, {});

export const getAppPackageId = () => {
    try {
        return DeviceInfo.getBundleId();
    } catch (_error) {
        return APP_VARIANTS.global.packageId;
    }
};

export const getAppVariantId = () => {
    const env = typeof process !== 'undefined' ? process.env : {};
    const configuredVariant = env?.EXPO_PUBLIC_HAPPY_APP_VARIANT || env?.HAPPY_APP_VARIANT;
    if (configuredVariant && APP_VARIANTS[configuredVariant]) {
        return configuredVariant;
    }

    const packageId = getAppPackageId();
    return PACKAGE_TO_VARIANT[packageId] || 'global';
};

/**
 * The desktop mini app this bundle was built as (`EXPO_PUBLIC_HAPPY_MINI_APP`),
 * or `null` for the full Happy App. See `MINI_APPS` in the manifest.
 * @returns {(typeof MINI_APPS)[keyof typeof MINI_APPS]|null}
 */
export const getMiniApp = () => {
    const id = resolveMiniAppIdFromEnv(typeof process !== 'undefined' ? process.env : {});
    return id ? MINI_APPS[id] : null;
};

export const isMiniApp = () => getMiniApp() !== null;

// A mini app bundles NO operational module (so none of the Management / POS
// providers or background pollers mount) and is pinned to the `manager` mode
// slot only so the mode machinery has a deterministic answer — every route
// decision is overridden by `getMiniApp().homeRoute` (see getRouteForAppMode and
// AppNavigator's initialRouteName).
const buildMiniAppVariant = (miniApp) => ({
    id: miniApp.id,
    name: miniApp.name,
    packageId: miniApp.identifier,
    allowedModes: ['manager'],
    defaultMode: 'manager',
    modules: {pos: false, sales: false, management: false, service: false, warehouse: false, driver: false},
    miniApp: miniApp.id,
});

export const getAppVariant = () => {
    const miniApp = getMiniApp();
    return miniApp ? buildMiniAppVariant(miniApp) : (APP_VARIANTS[getAppVariantId()] || APP_VARIANTS.global);
};

export const getVariantAllowedModes = () => getAppVariant().allowedModes;

export const getVariantDefaultMode = () => getAppVariant().defaultMode;

export const isGlobalAppVariant = () => getAppVariantId() === 'global';

export const isAppModeAllowedForVariant = (mode) => getVariantAllowedModes().includes(mode);

export const filterModesForAppVariant = (modes = []) => {
    const allowedModes = getVariantAllowedModes();
    return modes.filter((mode) => allowedModes.includes(mode) && isFeatureEnabled(`mode.${mode}`));
};

export const isAppModuleEnabled = (moduleName) => Boolean(getAppVariant().modules[moduleName]);

// Maps an operational app mode to the build module that hosts its screens. The
// home routes in MODULE_HOME_ROUTES are only registered when their module is
// bundled (see AppNavigator), so routing must never resolve to a disabled mode.
export const MODE_TO_MODULE = {
    // `restaurant` is the POS till; its module was renamed `sales` -> `pos` when
    // the CRM field-sales app took the `sales` name (see appVariantManifest).
    restaurant: 'pos',
    sales: 'sales',
    manager: 'management',
    service: 'service',
    stock: 'warehouse',
    driver: 'driver',
};

// True when the module hosting this mode's screens is bundled into the build AND
// the release profile keeps that mode (`mode.<name>` in config/featureFlags.js).
export const isAppModeEnabled = (mode) => {
    const moduleName = MODE_TO_MODULE[mode];
    return moduleName ? isAppModuleEnabled(moduleName) && isFeatureEnabled(`mode.${mode}`) : false;
};

// First variant-allowed mode whose module is actually bundled. Guarantees the
// caller receives a mode whose home screen is registered in the navigator.
export const getFirstUsableMode = () => {
    const allowedModes = getVariantAllowedModes();
    return allowedModes.find((mode) => isAppModeEnabled(mode)) || allowedModes[0] || 'restaurant';
};

export const resolveModeForAppVariant = (mode, fallbackMode = 'restaurant') => {
    const forcedMode = getVariantDefaultMode();
    if (forcedMode && isAppModeEnabled(forcedMode)) return forcedMode;

    if (mode && isAppModeAllowedForVariant(mode) && isAppModeEnabled(mode)) return mode;
    if (fallbackMode && isAppModeAllowedForVariant(fallbackMode) && isAppModeEnabled(fallbackMode)) return fallbackMode;
    return getFirstUsableMode();
};

export const getRouteForAppMode = (mode, isDayOpen = true) => {
    // Desktop mini apps open their one screen regardless of mode / day state.
    const miniApp = getMiniApp();
    if (miniApp) {
        return miniApp.homeRoute;
    }

    const resolvedMode = resolveModeForAppVariant(mode);
    if (resolvedMode === 'restaurant') {
        return isDayOpen ? getPosEntryRoute() : 'DayOpeningScreen';
    }

    return MODULE_HOME_ROUTES[resolvedMode] || 'HomeScreen';
};
